"use client";

import { ChevronDownIcon, ChevronUpIcon, WorkprideByMygworkIcon } from "icons";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { Heading, Text, Spinner, ImageWrapper } from "ui-components";

import { useAuth } from "@/hooks/useAuth";
import { MyGworkEventsWP } from "@/src/components/events/MyGworkEventsWP";
import { MediaPartners } from "@/src/components/MediaPartners/MediaPartners";
import { MembersSlider } from "@/src/components/members/MembersSlider";
import {
  UserResponseEntity,
  WorkPrideResponse,
  getOrganizationGetWorkPrideQueryKey,
  useOrganizationGetWorkPride,
} from "@/src/hooks/api/generated";

import DownloadBrochure from "./DownloadBrochure";
import Share from "./Share";

const asset_url =
  "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/workpride";

const sponsors = [
  {
    index: 0,
    href: "/organizations/organon",
    target: "_self",
    src: `${asset_url}/organon_ynpz2y.png`,
    alt: "Organon",
    width: 200,
    height: 101,
  },
  {
    index: 1,
    href: "/organizations/winston-taylor",
    target: "_self",
    src: `${asset_url}/258180e37e01d5aa1752c76136b6f0611709ba37_sqwbo2.png`,
    alt: "Winston Taylor",
    width: 162,
    height: 101,
  },
  {
    index: 2,
    href: "/organizations/rs-group",
    target: "_self",
    src: `${asset_url}/rsgruop_l2lph2.png`,
    alt: "RS Group",
    width: 100,
    height: 101,
  },
  {
    index: 3,
    href: "/organizations/canada-life-uk",
    target: "_blank",
    src: `${asset_url}/Canada_Life_Logo_jzrgkb.png`,
    alt: "Canada Life UK",
    width: 180,
    height: 100,
  },
  {
    index: 4,
    href: "/organizations/kyndryl",
    target: "_self",
    src: `${asset_url}/66dd302e8ea1453781125385c684e5d64e1f2c70_h29ktr.png`,
    alt: "Kyndryl",
    width: 160,
    height: 100,
  },
];

export default function WorkPridePage({
  initialData,
}: Readonly<{ initialData?: WorkPrideResponse }>): JSX.Element {
  const [showScroll, setShowScroll] = useState(false);
  const { authData } = useAuth();
  const userId = authData ? String(authData.id) : "0";
  const {
    data: workPrideOrganisationData,
    isLoading: isLoadingWorkPrideOrganisationData,
  } = useOrganizationGetWorkPride(userId, {
    query: {
      queryKey: getOrganizationGetWorkPrideQueryKey(userId),
      // Seed the anonymous (userId "0") render from the server fetch so the
      // schedule, hosts and speakers are in the initial HTML. Logged-in users
      // still refetch their personalised data on hydration.
      initialData: userId === "0" ? initialData : undefined,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

  useEffect(() => {
    if (isLoadingWorkPrideOrganisationData) {
      return undefined;
    }

    const handleHashChange = (): void => {
      const id = window.location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange, false);

    return (): void => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [isLoadingWorkPrideOrganisationData]);

  useEffect(() => {
    const handleScroll = (): void => {
      setShowScroll(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  if (isLoadingWorkPrideOrganisationData && !workPrideOrganisationData) {
    return (
      <div className="my-10 flex flex-col gap-y-10 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="my-10 flex flex-col gap-y-5 md:gap-y-8 items-center justify-center">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-col gap-y-5 items-center justify-center text-[#ffffff]">
          <div className="w-full h-full flex flex-col items-center justify-start gap-6 py-8 lg:flex-row md:gap-[60px] md:py-0 bg-white">
            <div className="flex flex-col items-center justify-start gap-6 md:gap-5xl">
              <div className="flex flex-col items-center justify-start gap-4">
                <Text className="text-center text-[#282120] !text-base !font-normal md:!text-lg">
                  Empowering the LGBTQ+ business community
                </Text>
                <Text className="bg-[#282120] p-4 !text-[#F4BF43] !font-bold !text-xl">
                  7th Edition
                </Text>
                <Heading
                  variant="h1"
                  className="!text-5xl !text-[#282120] !font-bold"
                >
                  WorkPride
                </Heading>
                <div className="flex flex-col items-center justify-start gap-6 md:gap-8">
                  <Text className="text-center text-[#282120] !font-normal !text-xl md:!text-2xl !leading-8">
                    The Largest Virtual Global Conference Dedicated to Workplace
                    Inclusion
                  </Text>
                  <Text className="text-center !text-[#282120] !font-normal !text-3xl md:!text-5xl">
                    Rewatch WorkPride 2026
                  </Text>
                  <div className="flex flex-col items-center justify-center gap-4">
                    <DownloadBrochure />
                    <Link
                      className="inline-flex items-center justify-center gap-3 w-[300px] md:w-[453px] h-[50px] md:h-auto rounded-[57px] px-0 py-4 md:px-10 md:py-[22px] outline outline-[2px] md:outline-[3px] outline-[#282120] outline-offset-[-2px] md:outline-offset-[-3px] text-[#282120] font-[500] break-words text-base md:text-lg"
                      href="/events?search=workpride+2026"
                    >
                      See Highlights
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Image
              src={`${asset_url}/workpride-2026-banner_tmktwp.webp`}
              alt="illustration"
              className="hidden md:block h-[392px] w-auto flex-1 self-auto max-w-[692px] rounded-[20px] mt-5"
              width={1180}
              height={392}
              unoptimized
            />

            <Image
              src={`${asset_url}/workpride-2026-banner-mobile_cxqahn.webp`}
              alt="illustration"
              className="block md:hidden h-auto w-auto rounded-[20px] self-stretch"
              width={1180}
              height={392}
              unoptimized
            />
          </div>
        </div>
      </div>
      {/* <div className="flex flex-col gap-y-5 items-center justify-center w-[100%] md:!w-[85%] text-[#ffffff] ">
        <div className="w-full h-full flex flex-col items-center justify-start gap-6 py-8 md:flex-row md:gap-6xl md:py-0 bg-white">
          <div className="flex flex-col items-center justify-start gap-6 md:gap-5xl">
            <div className="flex flex-col items-center justify-start gap-4">
              <Text className="text-center text-[#282120] !text-base !font-normal md:!text-lg">
                Empowering the LGBTQ+ business community
              </Text>
              <Text className="bg-[#282120] p-4 !text-[#F4BF43] !font-bold !text-xl">
                7th Edition
              </Text>

              <div className="flex flex-col items-center justify-start gap-6 md:gap-6 mt-4">
                <Heading
                  variant="h1"
                  className="!text-6xl md:!text-[64px] !text-[#282120] !font-bold"
                >
                  WorkPride
                </Heading>
                <Text className="text-center text-[#282120] !font-normal !text-xl md:!text-2xl md:px-14 !leading-8">
                  The Largest Virtual Global Conference Dedicated to Workplace
                  Inclusion
                </Text>
                <Text className="text-center !text-[#282120] !font-normal !text-3xl md:!text-[48px] md:my-4">
                  8-12 June 2026
                </Text>

                <Text className="text-center !text-[#282120] !font-normal !text-lg md:hidden">
                  #WorkingWithPride
                </Text>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-9">
              <Link
                className="inline-flex items-center justify-center gap-3 w-[300px] h-[50px] md:w-[250px] md:h-auto rounded-[57px] bg-[#F4BF43] px-5 py-4 md:px-10 md:py-[22px] outline outline-[2px] md:outline-[3px] outline-[#282120] outline-offset-[-2px] md:outline-offset-[-3px] text-[#282120] font-[500] break-words text-base md:text-lg"
                href="/workpride#5-days-of-events"
              >
                View Event Schedule
              </Link>

              <DownloadBrochure isEnquireWhite />
            </div>
          </div>

          <Image
            src={`${asset_url}/workpride-2026-banner_gcf7a6.png`}
            alt="illustration"
            className="hidden md:block h-[392px] w-auto flex-1 rounded-[20px] self-auto"
            width={1180}
            height={392}
            unoptimized
          />

          <Image
            src={`${asset_url}/workpride-2026-banner-mobile_mo0ear.png`}
            alt="illustration"
            className="block md:hidden h-[272px] w-auto rounded-[20px] self-stretch"
            width={1180}
            height={392}
            unoptimized
          />
        </div>
      </div> */}
      <Heading
        variant="h2"
        align="center"
        className="!text-3xl md:!text-5xl md:!mt-12 !text-[#282120]"
      >
        Inclusive Futures
      </Heading>
      <Heading
        variant="h4"
        align="center"
        className="mt-0 !text-[#282120] !text-lg md:!text-xl px-8 md:px-0 capitalize"
      >
        Building workplaces that enable every talent to thrive
      </Heading>
      <Text
        variant="base-thin"
        align="center"
        className="max-w-[813px] !text-lg px-2 md:px-0"
      >
        WorkPride 2026 is a free, award-winning five-day global event series for
        professionals, graduates, inclusive employers, and anyone committed to
        workplace equality. Each year, thousands of virtual attendees come
        together to connect, share best practices, and explore practical
        strategies for building truly inclusive workplaces — all identities and
        experiences are respected, represented, and empowered.
      </Text>
      <Heading
        variant="h4"
        align="center"
        className="mt-0 flex items-center justify-center gap-2 text-center text-xl !text-[#282120] px-8 md:px-0"
      >
        <Image
          src={`${asset_url}/gaming-award-prize_hv9ozb.png`}
          alt="illustration"
          className="w-6 h-6 md:w-8 md:h-8 object-contain"
          width={24}
          height={24}
          unoptimized
        />
        <span className="mx-2">
          Recognised by Rainbow Honour as Global LGBTQIA+ Initiative of the Year
        </span>
        <Image
          src={`${asset_url}/gaming-award-prize_hv9ozb.png`}
          alt="illustration"
          className="w-6 h-6 md:w-8 md:h-8 object-contain"
          width={24}
          height={24}
          unoptimized
        />
      </Heading>

      {/* <div className="video-container w-full">
        <iframe
          className="w-full h-[318px] md:w-[75%] md:h-[464px] mx-auto"
          src="https://player.vimeo.com/video/1095093081"
          title="Academy 2022!"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div> */}

      <div className="inline-flex items-center justify-center gap-[60px]">
        <WorkprideByMygworkIcon className="w-[100px] md:w-[168px]" />

        <Image
          src={`${asset_url}/rainbow-honors_ivxmma.png`}
          alt="illustration"
          className="w-[103px] md:w-[171px]"
          width={171}
          height={110}
          unoptimized
        />
      </div>

      <div className="mt-10 w-full bg-[#FEF9EC] py-20 flex flex-col gap-10 md:gap-[120px]">
        <div>
          <Heading
            variant="h2"
            align="center"
            className="!text-3xl !md:text-5xl !text-[#282120]"
          >
            Our Main Sponsor
          </Heading>
          <div className="flex items-center justify-center max-w-[680px] min-w-[275px] h-[86px] md:h-[146px] mx-auto mt-[30px] md:mt-12">
            <ImageWrapper
              src="/workpride/main-sponsor.png"
              alt="booking-com"
              width={680}
              height={86}
              className="block"
            />
          </div>
        </div>
        <div>
          <Heading
            variant="h2"
            align="center"
            className=" !text-3xl md:!text-5xl !text-[#282120] mb-10 md:mb-[60px]"
          >
            In 2026, we had
          </Heading>
          {/* <div className="relative max-w-[550px] h-[120px] mx-auto"> */}
          <div className="max-w-[1020px] w-full flex flex-col md:flex-row justify-center items-start gap-5 mx-auto">
            <div className="w-full flex flex-col gap-y-4 !gap-0 md:!gap-y-8 px-2.5">
              <Heading
                className="block font-normal !text-[#282120] !text-3xl md:!text-[56px]"
                variant="h2"
                align="center"
              >
                15,000+
              </Heading>
              <Text
                className="font-bold block !text-base md:!text-4xl !text-[#282120]"
                variant="base-bold"
                align="center"
              >
                Attendees
              </Text>
            </div>
            <div className="w-full flex flex-col gap-y-4 !gap-0 md:!gap-y-8 px-2.5">
              <Heading
                className="block font-normal !text-[#282120] !text-3xl md:!text-[56px]"
                variant="h2"
                align="center"
              >
                200+
              </Heading>
              <Text
                className="font-bold block !text-base md:!text-4xl !text-[#282120]"
                variant="base-bold"
                align="center"
              >
                Speakers
              </Text>
            </div>
            <div className="w-full flex flex-col gap-y-4 !gap-0 md:!gap-y-8 px-2.5">
              <Heading
                className="block font-normal !text-[#282120] !text-3xl md:!text-[56px]"
                variant="h2"
                align="center"
              >
                350+
              </Heading>
              <Text
                className="font-bold block !text-base md:!text-4xl !text-[#282120]"
                variant="base-bold"
                align="center"
              >
                Partners
              </Text>
            </div>
            <div className="w-full flex flex-col gap-y-4 !gap-0 md:!gap-y-8 px-2.5">
              <Heading
                className="block font-normal !text-[#282120] !text-3xl md:!text-[56px]"
                variant="h2"
                align="center"
              >
                45+
              </Heading>
              <Text
                className="font-bold block !text-base md:!text-4xl !text-[#282120]"
                variant="base-bold"
                align="center"
              >
                Webinars
              </Text>
            </div>
          </div>
        </div>
        <div>
          <Heading
            variant="h4"
            align="center"
            className="mb-10 sm:w-2/4 mx-auto !text-[20px] px-10 sm:!text-[36px] sm:!leading-[40px] !text-[#282120]"
          >
            Interested in sponsoring WorkPride or want to learn more?
          </Heading>
          <DownloadBrochure isEnquireBlue buttonText="Register Your Interest" />
        </div>
      </div>
      <div className="max-w-[1180px] mx-auto px-2">
        {/* <div className="w-full h-auto lg:h-[300px] hidden sm:block">
          <Image
            src={`${asset_url}/accelerating-inclusion_toadkv.png`}
            alt="illustration"
            width={1180}
            height={240}
            unoptimized
          />
        </div> */}
        <div className="md:!my-10 flex flex-col gap-y-10 w-full">
          <Heading
            variant="h2"
            align="center"
            className=" !text-3xl md:!text-5xl !text-[#282120]"
          >
            Our Inclusive Partners
          </Heading>
          <div className="w-full min-h-[312px]">
            <Link href="/organizations">
              <ImageWrapper
                src={`${asset_url}/Logo_Board_2025-winston-taylor_m3kuqb.png`}
                alt="illustration"
                width={1140}
                height={1059}
                unoptimized
              />
            </Link>
          </div>
        </div>
        <div className="relative w-full h-[100px] md:h-[300px] mt-8">
          <ImageWrapper
            src={`${asset_url}/workpride-2026-main-graphics_dv8twd.webp`}
            alt="illustration"
            fill
            objectFit="contain"
            unoptimized
          />
        </div>
      </div>
      <div className="flex flex-col gap-y-5 items-center justify-center w-[100%] md:!w-[85%] max-w-[1180px]">
        {workPrideOrganisationData?.events && (
          <>
            <Heading
              variant="h2"
              align="center"
              className="!text-3xl md:!text-5xl mt-12 !text-[#282120]"
              id="5-days-of-events"
            >
              {Object.keys(workPrideOrganisationData?.events).length} Days of
              Events
            </Heading>
            <Link
              className="!bg-[#F4BF43] text-[#282120] !w-64 py-4 rounded-full flex justify-center items-center hover:shadow-lg text-lg  border-[#282120] border-2"
              href="/events?search=workpride+2026"
            >
              View all WorkPride Events
            </Link>
            {Object.entries(workPrideOrganisationData?.events).map(
              ([key, value], index) => {
                if (!value.length) {
                  return null;
                }
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-y-5 max-w-[100%] items-center bg-background-dark bg-opacity-20 py-10 rounded-3xl"
                    id={`${index + 1}-day-of-events`}
                  >
                    <div className="w-full flex md:flex-row px-8 justify-between items-start md:items-center">
                      <div className="flex flex-col items-start  gap-y-2">
                        <Heading
                          variant="h3"
                          align="left"
                          className="block !text-2xl"
                        >
                          Day {index + 1}
                        </Heading>
                        <Heading
                          variant="h3"
                          align="left"
                          className="block !text-4xl !text-[#282120]"
                        >
                          {key}
                        </Heading>
                        <Share day={`${index + 1}-day-of-events`} />
                      </div>
                      {sponsors.map((sponsor) =>
                        index === sponsor.index ? (
                          <div
                            key={sponsor.index}
                            className="flex flex-col items-end justify-end text-right"
                          >
                            <Text
                              variant="large"
                              align="right"
                              className="mb-2"
                            >
                              Sponsored by:
                            </Text>
                            <Link
                              href={sponsor.href}
                              className="py-2"
                              target={sponsor.target}
                            >
                              <ImageWrapper
                                src={sponsor.src}
                                alt={sponsor.alt}
                                height={sponsor.height}
                                width={sponsor.width}
                                unoptimized
                              />
                            </Link>
                          </div>
                        ) : null
                      )}
                    </div>
                    <div className="mt-5 w-[90%] ">
                      <MyGworkEventsWP events={value} isWorkpride />
                    </div>
                  </div>
                );
              }
            )}
          </>
        )}
      </div>

      <Link
        className="!bg-[#F4BF43] text-[#282120] !w-64 py-4 rounded-full flex justify-center items-center hover:shadow-lg text-lg border-[#282120] border-2"
        href="/events?search=workpride+2026"
      >
        View all WorkPride Events
      </Link>
      <Heading
        variant="h2"
        align="center"
        className="lg:text-5xl !text-[#282120] pt-12 pb-2"
      >
        Meet our Hosts
      </Heading>
      <div className="max-w-[90%] md:!max-w-[1220px]">
        <MembersSlider
          type="hosts"
          users={
            workPrideOrganisationData?.hosts
              ? (workPrideOrganisationData.hosts as UserResponseEntity[])
              : []
          }
        />
      </div>
      <Heading
        variant="h2"
        align="center"
        className="lg:text-5xl !text-[#282120] pt-12 pb-2"
      >
        Meet our Speakers
      </Heading>
      <div className="max-w-[100%] md:!max-w-[1228px]">
        <MembersSlider
          type="speakers"
          users={
            workPrideOrganisationData?.speakers
              ? (workPrideOrganisationData.speakers as UserResponseEntity[])
              : []
          }
        />
      </div>
      <div className="max-w-[1180px] mx-auto px-2">
        <div className="md:!my-10 flex flex-col gap-y-10 w-full">
          <Heading
            variant="h2"
            align="center"
            className=" !text-3xl md:!text-5xl !text-[#282120]"
          >
            FAQ
          </Heading>
          <div className="w-full">
            <Accordion key="one" title="How do I register for WorkPride 2026?">
              <Text className="!text-lg !font-[400]">
                You can register directly through the WorkPride 2026 event page.
                Simply choose the sessions you’d like to attend and complete the
                registration form. Once registered, you’ll receive an email
                confirming your sign-up.
              </Text>
            </Accordion>
            <Accordion
              key="two"
              title="I registered—how do I join the sessions?"
            >
              <>
                <Text className="!text-lg !font-[400] block">
                  All sessions are hosted online.
                </Text>
                <Text className="!text-lg !font-[400] mb-4 block">
                  Before each session, you’ll receive:
                </Text>

                <ul className="list-disc pl-6 space-y-3">
                  <li className="text-lg font-[400] text-black">
                    A confirmation email right after registration
                  </li>
                  <li className="text-lg font-[400] text-black">
                    Automatic reminder emails leading up to the event
                  </li>
                  <li className="text-lg font-[400] text-black">
                    A unique access link for each session you signed up for
                  </li>
                </ul>

                <Text className="!text-lg !font-[400] mt-4 block">
                  Just click the link at the session start time—no login
                  required.
                </Text>
              </>
            </Accordion>
            <Accordion
              key="three"
              title="What if I didn’t receive my confirmation or reminder emails?"
            >
              <Text className="!text-lg !font-[400]">
                Check your spam or promotions folder first. If you still can’t
                find them, you can reach out to our support team, and we’ll
                resend your session links.
              </Text>
            </Accordion>
            <Accordion
              key="four"
              title="Do I need to download anything to attend the sessions?"
            >
              <Text className="!text-lg !font-[400]">
                No downloads are required. Sessions are hosted through an online
                platform that runs in your browser. We recommend using an
                up-to-date version of Chrome, Firefox, Edge, or Safari.
              </Text>
            </Accordion>
            <Accordion
              key="five"
              title="Will recordings be available after the conference?"
            >
              <Text className="!text-lg !font-[400]">
                Yes, all sessions (unless otherwise noted) will be recorded. The
                recordings will be available on the event site within two weeks
                after the conference ends.
              </Text>
            </Accordion>
            <Accordion
              key="six"
              title="How can I obtain a certificate of attendance?"
            >
              <>
                <Text className="!text-lg !font-[400]">
                  If you attend 5 or more sessions, you will automatically
                  qualify for a WorkPride 2026 Certificate of Attendance.
                </Text>
                <Text className="!text-lg block mt-6 !font-bold">
                  No action is needed from you.
                </Text>
                <Text className="!text-lg !font-[400] block">
                  We will send certificates via email one week after the
                  conference concludes.
                </Text>
              </>
            </Accordion>
            <Accordion
              key="seven"
              title="How do you track my attendance for the certificate?"
            >
              <Text className="!text-lg !font-[400]">
                Attendance is tracked with your email address. Make sure you
                register and join the sessions using the same email address.
              </Text>
            </Accordion>
            <Accordion
              key="eight"
              title="Who can I contact for help before or during WorkPride?"
            >
              <Text className="!text-lg !font-[400]">
                Our support team is available to assist you with registration
                issues, access links, accessibility needs, or general questions.
                You can email them at{" "}
                <a href="mailto: workpride@mygwork.com">
                  workpride@mygwork.com
                </a>
                .
              </Text>
            </Accordion>
          </div>
        </div>
      </div>
      <div className="items-center bg-[#FEF9EC] w-full md:my-10 px-5">
        <div className="max-w-[1180px] mx-auto flex flex-col md:gap-5 sm:flex-row justify-between">
          <div className="py-4 px-5 md:py-20 md:!w-[50%] w-[100%]">
            <Heading
              className="font-bold !leading-[2.5rem] hidden sm:block !text-[#282120] !text-4xl"
              variant="h2"
            >
              Receive your LGBTQ+ Inclusion Certificate of Attendance
            </Heading>
            <Text className="text-center !font-bold sm:text-left sm:!font-normal inline-block mt-8 !text-lg">
              Receive a LGBTQ+ Inclusion Certificate of Attendance when
              registering and attending at least 5 WorkPride Conference Webinars
            </Text>
          </div>
          <div className="relative md:h-[355px] md:!w-[30%] w-[100%] flex items-center justify-center">
            <ImageWrapper
              src={`${asset_url}/certificate_yw6t9w.png`}
              alt="certificate"
              width={362}
              height={272}
              unoptimized
            />
          </div>
        </div>
      </div>
      <div className="max-w-[1180px] mx-auto px-2">
        <div className="md:!my-10 flex flex-col gap-y-10 w-full">
          <Heading
            variant="h2"
            align="center"
            className=" !text-3xl md:!text-5xl !text-[#282120]"
          >
            Our Community Partners
          </Heading>
          <div className="w-full min-h-[312px] max-h-[1028px] lg:h-[955px]">
            <ImageWrapper
              src={`${asset_url}/community-partners-v3_xjevu8.webp`}
              alt="illustration"
              width={1028}
              height={761}
              unoptimized
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-5 items-center justify-center w-[100%] md:!w-[85%] max-w-[1180px]">
        <Heading
          variant="h2"
          align="center"
          className=" !text-3xl md:!text-5xl !text-[#282120]"
        >
          Our Media Partners
        </Heading>
        <div className="w-full media-partners">
          <MediaPartners
            partners={{
              partner1: `${asset_url}/media_partner_1_oajivd.png`,
              partner2: `${asset_url}/media_partner_2_yp4bv2.png`,
              partner3: `${asset_url}/media_partner_3_sv8cwt.png`,
              partner4: `${asset_url}/media_partner_5_ze7vqy.png`,
              partner5: `${asset_url}/media_partner_4_lmvsvn.png`,
              partner6: `${asset_url}/media_partner_6_hgos4f.png`,
              partner7: `${asset_url}/media_partner_7_jjkgtt.png`,
              partner8: `${asset_url}/Network_Horizontal_Black_1_yvnjrd.png`,
            }}
          />
        </div>
        <div className="w-full md:h-[20rem]">
          <div className="relative w-full h-[100px] md:h-[300px]">
            <ImageWrapper
              src={`${asset_url}/workpride-2026-main-graphics_dv8twd.webp`}
              alt="illustration"
              fill
              objectFit="contain"
              unoptimized
            />
          </div>
        </div>
      </div>
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 !bg-none rounded-md transition hover:scale-110 flex flex-col items-center justify-center"
        >
          <img
            src={`${asset_url}/back-to-top_jpntvl.png`}
            alt="Back to Top"
            className="w-[40px] h-[40px]"
          />
          <span className="text-sm mt-1">back to top</span>
        </button>
      )}
    </div>
  );
}

const Accordion = ({
  title,
  children,
}: {
  title: string;
  children: JSX.Element;
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-[#FEF9EC] rounded-2xl mt-5 md:w-[800px]">
      <button
        className="w-full bg-[#FEF9EC] text-left p-6 focus:outline-none rounded-2xl"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex justify-between items-center">
          <div>
            <Text className="!text-xl font-semibold">{title}</Text>
          </div>
          <span>
            {isOpen ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </span>
        </div>
      </button>
      {isOpen && (
        <div className="p-6 pt-2 !text-lg !leading-none bg-[#FEF9EC] rounded-2xl">
          <div className="w-11/12">{children}</div>
        </div>
      )}
    </div>
  );
};
